import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { invalidateLocalityCache } from 'src/api/localities'
import { invalidateMaintenanceCache } from 'src/api/maintenance'
import { invalidateScheduledMaintenanceCache } from 'src/api/scheduled-maintenance'
import { invalidateServiceStationCache } from 'src/api/service-station'
import { MainComponentStatus } from 'src/enums'
import {
  invalidateMainComponentCache,
  useGetMainComponentById,
  useUpdateMainComponent,
} from '../../../api/main-component'
import { IMainComponent } from '../../../generated-types/main-component'
import { ShowDialogProps } from '../../../show-dialog'
import { getTranslatedText } from '../../../utils/translations'
import MainComponentForm from './MainComponentForm'

interface UpdateMainComponentProps extends ShowDialogProps {
  id: EntityId
  mainComponentTypeName: string
  customerId: EntityId
  localityId?: EntityId
  searchText?: string
  serviceStationId?: EntityId
  isCustomerSelectionEnable?: boolean
  isUpdateCustomerEnable?: boolean
  serviceStationOwnerId?: EntityId
}

export const UpdateMainComponentDialog: React.FC<UpdateMainComponentProps> = ({
  pop,
  id,
  mainComponentTypeName,
  customerId,
  localityId,
  searchText,
  serviceStationId,
  isCustomerSelectionEnable,
  isUpdateCustomerEnable,
  serviceStationOwnerId,
}) => {
  const { t: translate } = useTranslation(['main-component', 'common'])
  const { mutateAsync, isLoading } = useUpdateMainComponent(customerId as EntityId, id as EntityId)

  const { data } = useGetMainComponentById(customerId, id)
  const [selectedCustomerId, setSelectedCustomerId] = useState<EntityId | undefined>(data?.customerId)

  const resetFormData = form => {
    if (selectedCustomerId && selectedCustomerId == data?.customerId) {
      form.reset({
        ...form.formState.defaultValues,
        typeId: data?.typeId,
        supplierId: data?.supplierId,
        positionId: data?.positionId,
        customPropertyValues: data?.customPropertyValues,
        mainComponentMaintenanceTemplates: data?.mainComponentMaintenanceTemplates,
      })
    } else {
      form.setValue('typeId', null)
      form.setValue('supplierId', null)
      form.setValue('positionId', null)
      form.setValue('customPropertyValues', '{}')
      form.setValue('mainComponentMaintenanceTemplates', undefined)
    }
  }

  const onSubmit = async form => {
    await mutateAsync(
      { ...form, customerId: isCustomerSelectionEnable ? selectedCustomerId : data?.customerId },
      {
        onSuccess: () => {
          const cId = selectedCustomerId ?? customerId
          invalidateMainComponentCache.getMainComponentsByComponentType(cId, form.typeId, searchText)
          invalidateMainComponentCache.getMainComponentsByComponentType(cId as EntityId, form.typeId)
          invalidateMainComponentCache.getMainComponentByIdAdmin(customerId, id)
          invalidateMainComponentCache.getMainComponentByIdAdmin(selectedCustomerId ?? customerId, id)
          invalidateMainComponentCache.getMainComponentById(customerId as EntityId, id as EntityId)
          invalidateMainComponentCache.getMainComponentById(selectedCustomerId as EntityId, id as EntityId)
          invalidateMainComponentCache.getEquipment(selectedCustomerId ?? customerId)
          invalidateLocalityCache.getLocality(selectedCustomerId ?? customerId, localityId as EntityId)
          invalidateMainComponentCache.getMainComponents(
            selectedCustomerId ?? customerId,
            localityId as EntityId,
            (data as IMainComponent)?.position?.mooringId,
            (data as IMainComponent)?.positionId,
          )
          invalidateScheduledMaintenanceCache.invalidateAllScheduledMaintenances(
            selectedCustomerId ?? customerId,
            localityId as EntityId,
          )
          invalidateMaintenanceCache.invalidateAllMaintenances(customerId, localityId as EntityId)
          invalidateMainComponentCache.getMaincomponentsBySearch(selectedCustomerId ?? customerId, searchText)
          invalidationActivityLogCache.getCageActivityLogs()
          invalidateServiceStationCache.getComponentsByStatus(
            (serviceStationOwnerId ?? customerId) as EntityId,
            serviceStationId as EntityId,
            MainComponentStatus.ReadyToDelivery,
          )
          invalidateServiceStationCache.getComponentsByStatus(
            (serviceStationOwnerId ?? customerId) as EntityId,
            serviceStationId as EntityId,
            MainComponentStatus.InService,
          )
          invalidateServiceStationCache.getComponentsByStatus(
            (serviceStationOwnerId ?? customerId) as EntityId,
            serviceStationId as EntityId,
            MainComponentStatus.Incoming,
          )
          invalidateServiceStationCache.getComponentsByStatus(
            (serviceStationOwnerId ?? customerId) as EntityId,
            serviceStationId as EntityId,
            MainComponentStatus.InStock,
          )
          invalidateServiceStationCache.searchCustomersForGlobalServiceStation(customerId as EntityId, serviceStationId)
          invalidateMainComponentCache.getMaincomponentsBySearchWithFilters(customerId as EntityId, searchText)
          invalidateLocalityCache.getCageStatus(customerId, localityId)
          invalidateLocalityCache.getLocality(customerId, localityId as EntityId)
          invalidateServiceStationCache.getCustomerWithComponentCountById(
            customerId as EntityId,
            serviceStationId as EntityId,
          )
          toast.success(translate('successfullyUpdated', { ns: 'common-api-message' }))
          pop(true)
        },
        onError: e => {
          toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
        },
      },
    )
  }

  return (
    <>
      {data && (
        <MainComponentForm
          title={
            mainComponentTypeName
              ? `${translate('updateMainComponent')} - ${getTranslatedText(mainComponentTypeName)}`
              : translate('updateMainComponent')
          }
          onSubmit={onSubmit}
          onCancel={() => pop()}
          initialValues={
            {
              ...data,
              mainComponentTypeName: mainComponentTypeName,
            } as IMainComponent
          }
          customerId={customerId}
          serviceStationId={serviceStationId}
          selectedCustomerId={selectedCustomerId}
          setSelectedCustomerId={setSelectedCustomerId}
          isCustomerSelectionEnable={isCustomerSelectionEnable}
          isUpdateCustomerEnable={isUpdateCustomerEnable}
          isSubmitting={isLoading}
          resetFormData={resetFormData}
        />
      )}
    </>
  )
}
